<template>
  <div class="page new-game">
    <h1 class="page-title">New game</h1>
    <p class="page-lede">Players choose games in seat order, starting with seat 1.</p>
    <p v-if="store.hasActiveGame && store.rounds.length > 0" class="saved-note">
      Your current game is saved in
      <router-link to="/history">History</router-link>, so you can go back to it later.
    </p>

    <form class="setup" @submit.prevent="submit">
      <fieldset class="field">
        <legend class="field-label">Players</legend>
        <div class="segmented" role="radiogroup" aria-label="Number of players">
          <button
            v-for="count in [3, 4, 5, 6]"
            :key="count"
            type="button"
            role="radio"
            :aria-checked="playerCount === count"
            :class="{ 'is-active': playerCount === count }"
            @click="updatePlayerCount(count)"
          >
            {{ count }}
          </button>
        </div>
      </fieldset>

      <fieldset ref="namesField" class="field" @focusout="handleFocusOut">
        <legend class="field-label">Names</legend>
        <ol class="names">
          <li
            v-for="n in playerCount"
            :key="n"
            class="name-row"
            :class="{ 'is-changed': changedSeats.includes(n - 1), 'is-target': selected === n - 1 }"
          >
            <button
              type="button"
              class="seat"
              :class="{
                'is-selected': isPickedSeat(n - 1),
                'is-swap-option': selectedVia === 'seat' && !isPickedSeat(n - 1)
              }"
              :aria-pressed="isPickedSeat(n - 1)"
              :aria-label="seatLabel(n - 1)"
              @click="pickSeat(n - 1)"
            >
              {{ n }}
            </button>
            <div class="name-input">
              <input
                :id="'player' + n"
                :ref="(el) => (inputs[n - 1] = el)"
                v-model="playerNames[n - 1]"
                class="text-input"
                :class="{ 'is-invalid': isNameDuplicate(playerNames[n - 1]) }"
                :placeholder="`Player ${n}`"
                :aria-label="`Seat ${n} name`"
                autocomplete="off"
                autocapitalize="words"
                enterkeyhint="next"
                :aria-describedby="isNameDuplicate(playerNames[n - 1]) ? `dup${n}` : undefined"
                @keydown.enter.prevent="focusNext(n)"
                @focus="selectSeat(n - 1, 'input')"
              />
              <span v-if="isNameDuplicate(playerNames[n - 1])" :id="`dup${n}`" class="error">
                Another player already has this name
              </span>
            </div>
          </li>
        </ol>
        <p class="hint seat-hint" aria-live="polite">{{ seatHint }}</p>

        <div v-if="visibleSuggestions.length" class="suggestions">
          <p id="suggestions-label" class="suggestions-label">Played before</p>
          <ul class="chips" aria-labelledby="suggestions-label">
            <li v-for="suggestion in visibleSuggestions" :key="suggestion.key">
              <button
                type="button"
                class="chip"
                :class="{ 'is-seated': suggestion.seat !== -1 }"
                :disabled="suggestion.seat === selected"
                :aria-label="chipLabel(suggestion)"
                @pointerdown.prevent
                @click="placeName(suggestion.name)"
              >
                {{ suggestion.name }}
              </button>
            </li>
          </ul>
        </div>
      </fieldset>

      <fieldset class="field">
        <legend class="field-label">Games</legend>
        <div class="segmented">
          <label>
            <input v-model="gameType" type="radio" name="gameType" value="full" />
            <span>All eight</span>
          </label>
          <label>
            <input v-model="gameType" type="radio" name="gameType" value="short" />
            <span>Choose games</span>
          </label>
        </div>
        <p class="hint">
          {{
            gameType === 'full'
              ? `${playerCount * 8} rounds: every player picks each game once.`
              : 'Play a shorter game with only the games you pick.'
          }}
        </p>
      </fieldset>

      <div class="submit">
        <button type="submit" class="btn btn-primary btn-block" :disabled="!isValid">
          {{ gameType === 'full' ? 'Start game' : 'Choose games' }}
        </button>
        <p v-if="!isValid" class="hint" aria-live="polite">{{ invalidReason }}</p>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useGameStore } from '../stores/game';

const router = useRouter();
const store = useGameStore();

const previousNames = store.playerNames;
const playerCount = ref(previousNames.length >= 3 ? previousNames.length : 4);
const playerNames = ref(Array.from({ length: 6 }, (_, i) => previousNames[i] ?? ''));
const gameType = ref('full');
const inputs = ref([]);
const selected = ref(null);
const selectedVia = ref(null);
const changedSeats = ref([]);
let changeTimer = null;

onBeforeUnmount(() => clearTimeout(changeTimer));

const seatKey = (name) => name.trim().toLowerCase();
const nameAt = (index) => playerNames.value[index].trim();
const activeNames = () => playerNames.value.slice(0, playerCount.value);

const namesField = ref(null);

const isPickedSeat = (index) => selectedVia.value === 'seat' && selected.value === index;

const selectSeat = (index, via) => {
  selected.value = index;
  selectedVia.value = via;
};

const clearSelection = () => {
  selected.value = null;
  selectedVia.value = null;
};

const flash = (seats) => {
  changedSeats.value = seats;
  clearTimeout(changeTimer);
  changeTimer = setTimeout(() => (changedSeats.value = []), 700);
};

const swapSeats = (a, b) => {
  const names = [...playerNames.value];
  [names[a], names[b]] = [names[b], names[a]];
  playerNames.value = names;
  flash([a, b]);
};

const pickSeat = (index) => {
  if (selectedVia.value !== 'seat') {
    selectSeat(index, 'seat');
    return;
  }
  const from = selected.value;
  clearSelection();
  if (from !== index) swapSeats(from, index);
};

const nextEmptySeatAfter = (index) => {
  const names = activeNames();
  const order = [...names.keys()].map((i) => (index + 1 + i) % names.length);
  return order.find((i) => names[i].trim() === '') ?? null;
};

const placeName = (name) => {
  const seat = selected.value;
  if (seat === null) return;
  const current = activeNames().findIndex((other) => seatKey(other) === seatKey(name));
  if (current === seat) return;
  if (current !== -1) {
    swapSeats(current, seat);
  } else {
    const names = [...playerNames.value];
    names[seat] = name;
    playerNames.value = names;
    flash([seat]);
  }
  const wasTyping = selectedVia.value === 'input';
  const next = nextEmptySeatAfter(seat);
  if (next === null) {
    clearSelection();
    if (wasTyping) document.activeElement?.blur();
  } else if (wasTyping) {
    inputs.value[next]?.focus();
  } else {
    selectSeat(next, 'auto');
  }
};

const seatLabel = (index) => {
  const who = nameAt(index) || 'empty';
  if (selectedVia.value !== 'seat') return `Seat ${index + 1}, ${who}. Select`;
  if (selected.value === index) return `Seat ${index + 1}, ${who}. Selected, tap to cancel`;
  return `Swap seat ${selected.value + 1} with seat ${index + 1}, ${who}`;
};

const chipLabel = (suggestion) => {
  if (selected.value === null) return suggestion.name;
  const where = `seat ${selected.value + 1}`;
  return suggestion.seat === -1
    ? `Put ${suggestion.name} in ${where}`
    : `Move ${suggestion.name} from seat ${suggestion.seat + 1} to ${where}`;
};

const suggestions = computed(() => {
  const names = activeNames().map(seatKey);
  const tally = new Map();
  store.history.forEach((game, recency) => {
    game.players?.forEach((player) => {
      const name = player.name?.trim();
      if (!name) return;
      const key = seatKey(name);
      const entry = tally.get(key) ?? { key, name, count: 0, recency };
      entry.count += 1;
      tally.set(key, entry);
    });
  });
  return [...tally.values()]
    .sort((a, b) => b.count - a.count || a.recency - b.recency)
    .slice(0, 12)
    .map((entry) => ({ ...entry, seat: names.indexOf(entry.key) }));
});

const visibleSuggestions = computed(() => {
  if (selected.value === null) return [];
  const typed = selectedVia.value === 'input' ? seatKey(playerNames.value[selected.value]) : '';
  if (!typed) return suggestions.value;
  return suggestions.value.filter((entry) => entry.key.startsWith(typed) && entry.key !== typed);
});

const handleFocusOut = (event) => {
  if (selectedVia.value !== 'input') return;
  if (namesField.value?.contains(event.relatedTarget)) return;
  clearSelection();
};

const seatHint = computed(() => {
  const hasNames = suggestions.value.length > 0;
  if (selectedVia.value === 'seat') {
    const seat = selected.value + 1;
    return hasNames
      ? `Seat ${seat} selected. Tap a name below, or another seat number to swap.`
      : `Tap another seat number to swap it with seat ${seat}.`;
  }
  if (visibleSuggestions.value.length) {
    return `Tap a name below to put it in seat ${selected.value + 1}.`;
  }
  if (selectedVia.value === 'input') return '';
  return hasNames
    ? 'Tap a seat number to swap seats or pick someone who played before.'
    : 'Tap two seat numbers to swap those players.';
});

const duplicateNames = computed(() => {
  const keys = activeNames().map(seatKey).filter(Boolean);
  return new Set(keys.filter((key, i) => keys.indexOf(key) !== i));
});

const isNameDuplicate = (name) => duplicateNames.value.has(seatKey(name));

const emptyCount = computed(() => activeNames().filter((name) => name.trim() === '').length);

const isValid = computed(() => emptyCount.value === 0 && duplicateNames.value.size === 0);

const invalidReason = computed(() => {
  if (duplicateNames.value.size > 0) return 'Give each player a different name.';
  if (emptyCount.value === playerCount.value) return 'Enter a name for each player.';
  if (emptyCount.value > 0)
    return `${emptyCount.value} more name${emptyCount.value === 1 ? '' : 's'} to go.`;
  return '';
});

const updatePlayerCount = (count) => {
  playerCount.value = count;
  if (selected.value !== null && selected.value >= count) clearSelection();
};

const focusNext = (n) => {
  if (n < playerCount.value) {
    inputs.value[n]?.focus();
  } else if (isValid.value) {
    submit();
  }
};

const submit = () => {
  if (!isValid.value) return;
  store.initializeGame(activeNames());
  router.push(gameType.value === 'full' ? '/game' : '/select-mini-games');
};
</script>

<style scoped>
.page-lede {
  margin-bottom: 1rem;
}

.saved-note {
  margin-bottom: 1.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: var(--radius-control);
  border: 1px solid var(--panel-edge);
  background-color: var(--panel-solid);
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.saved-note a {
  color: var(--on-table);
  font-weight: 600;
}

.setup {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.field {
  border: none;
  min-width: 0;
}

.field-label {
  padding: 0;
}

.names {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.name-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.seat {
  flex: none;
  width: 2.75rem;
  height: 3rem;
  display: grid;
  place-items: center;
  border-radius: var(--radius-control);
  border: 1px solid transparent;
  background-color: var(--table-raised);
  color: var(--on-table-muted);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.375rem;
  transition:
    background-color 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.seat:hover {
  background-color: var(--table-hover);
  color: var(--on-table);
}

.seat.is-swap-option {
  border-style: dashed;
  border-color: var(--on-table-muted);
  color: var(--on-table);
}

.seat.is-selected {
  background-color: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}

.is-target .text-input {
  border-color: var(--on-table-muted);
}

.is-changed .text-input {
  animation: changed 0.7s ease-out;
}

@keyframes changed {
  from {
    border-color: var(--on-table);
    background-color: var(--table-raised);
  }
}

.seat-hint {
  margin-top: 0.625rem;
}

.suggestions {
  margin-top: 1.25rem;
}

.suggestions-label {
  font-size: var(--step--1);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.chips {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.chip {
  min-height: 40px;
  padding: 0 0.875rem;
  border-radius: 999px;
  border: 1px solid var(--panel-edge);
  background-color: var(--table-raised);
  color: var(--on-table);
  font-weight: 500;
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.chip:hover:not(:disabled) {
  background-color: var(--table-hover);
}

.chip.is-seated {
  background-color: transparent;
  color: var(--on-table-muted);
  border-style: dashed;
}

.chip:disabled {
  opacity: 0.4;
}

.name-input {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.error {
  color: var(--red-on-table);
  font-size: var(--step--1);
}

.hint {
  margin-top: 0.5rem;
  color: var(--on-table-muted);
  font-size: var(--step--1);
}

.submit {
  text-align: center;
}

@media (min-width: 768px) {
  .new-game {
    max-width: 560px;
  }
}
</style>
